import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FileQuestion, Wrench } from 'lucide-react';
import { backend } from '@/api/backend';
import { ApiError } from '@/api/http';
import { explainLine } from '@/api/explain';
import { Button, Skeleton, Stamp, ReadFailed } from '@/components/ui';
import { ToolIcon } from '@/components/ToolIcon';
import { say } from '@/lib/vocabulary';
import { cn } from '@/lib/cn';

/**
 * The artifact-attribution ledger: every object and its one terminal answer.
 *
 * The workflow contract asks for this by name. Every item in a sealed run's
 * manifest gets exactly one disposition, TOOL or STRAY, and the two sets are
 * disjoint and together equal the manifest. Until now the product showed only
 * the totals: a conservation bar and a chart. An operator could see that 41,451
 * files were untraced and could not look at one of them.
 *
 * Both halves are read from the sealed run, filterable by which answer they
 * got, and every row carries the evidence reference that justifies it. Nothing
 * here is derived: the reason on a stray row is the walk's own closed reason
 * kind, and the rule on a tool row is the rule that matched.
 *
 * Keyset paged, and it says when it is showing a page rather than the whole
 * ledger, because a ledger that quietly stops at fifty rows is worse than no
 * ledger at all.
 */

const PAGE = 100;

type Side = 'TOOL' | 'STRAY';

interface ToolRow {
  objectKey: string;
  toolId: string;
  ruleId?: string | null;
  evidenceRef?: string | null;
}

interface StrayRow {
  objectKey: string;
  reasonKind: string;
  basis?: string | null;
  evidenceRef?: string | null;
}

function readPage(v: unknown): { items: Record<string, unknown>[]; nextCursor: string | null; sealedAt?: string } | null {
  const d = ((v as { data?: unknown })?.data ?? v) as Record<string, unknown> | null;
  if (!d || !Array.isArray(d.items)) return null;
  return {
    items: d.items as Record<string, unknown>[],
    nextCursor: typeof d.nextCursor === 'string' ? d.nextCursor : null,
    sealedAt: typeof d.sealedAt === 'string' ? d.sealedAt : undefined,
  };
}

export function AttributionLedger({ bucketId }: { bucketId: string }) {
  const [side, setSide] = useState<Side>('STRAY');

  const q = useInfiniteQuery({
    queryKey: ['attribution-ledger', bucketId, side],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }): Promise<unknown> =>
      side === 'TOOL'
        ? await backend.listObjectAttributions(bucketId, { pageSize: PAGE, cursor: pageParam }, signal)
        : await backend.listStrayAttributions(bucketId, { pageSize: PAGE, cursor: pageParam }, signal),
    getNextPageParam: (last) => readPage(last)?.nextCursor ?? undefined,
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 30_000,
  });

  const pages = (q.data?.pages ?? []).map(readPage);
  const drift = pages.some((p) => p === null);
  const rows = pages.flatMap((p) => p?.items ?? []);
  const sealedAt = pages.find((p) => p?.sealedAt)?.sealedAt;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        {(['STRAY', 'TOOL'] as const).map((s) => (
          <button
            key={s}
            // Selection was colour and a border only, so a screen reader read
            // two identical buttons and no indication of which side of the
            // ledger was showing. This group is a choice, and it says so.
            aria-pressed={side === s}
            onClick={() => setSide(s)}
            className={cn(
              'flex items-center gap-1.5 rounded border px-2.5 py-1 text-caption transition-colors duration-fast',
              side === s ? 'border-acc bg-acc-soft text-acc-2' : 'border-line bg-panel-2 text-ink-3 hover:bg-hov',
            )}
          >
            {s === 'TOOL' ? <Wrench className="h-3 w-3" aria-hidden="true" /> : <FileQuestion className="h-3 w-3" aria-hidden="true" />}
            {s === 'TOOL' ? 'traced to a tool' : 'traced to nothing'}
          </button>
        ))}
        <span className="ml-auto text-caption text-ink-3">
          {sealedAt ? `from the walk sealed ${sealedAt.slice(0, 10)}` : 'from the newest sealed walk'}
        </span>
      </div>

      <RuleSet />

      {q.isPending && <Skeleton className="mx-4 my-3 h-5" />}

      {q.isError && (
        <ReadFailed what={explainLine(q.error, 'read the attribution ledger')} onRetry={() => void q.refetch()} />
      )}

      {/* A page that does not parse is drift. Drawing zero rows for it would
          read as a bucket where nothing was attributed, which is the opposite
          of not knowing. */}
      {drift && (
        <p className="px-4 py-3 text-small text-warn">
          The ledger came back in a shape this screen does not recognise, so nothing is drawn. Any count here would be a
          guess.
        </p>
      )}

      {!q.isPending && !q.isError && !drift && rows.length === 0 && (
        <p className="px-4 py-3 text-small text-ink-3">
          {side === 'TOOL'
            ? 'This walk traced no file to any tool.'
            : 'Every file this walk saw was traced to a tool, so nothing is stray.'}
        </p>
      )}

      {rows.map((raw, i) => {
        const key = String(raw.objectKey ?? '');
        const evidence = typeof raw.evidenceRef === 'string' ? raw.evidenceRef : null;
        if (side === 'TOOL') {
          const r = raw as unknown as ToolRow;
          return (
            <div key={`${key}:${i}`} className="flex items-center gap-3 border-b border-line px-4 py-1.5 last:border-0">
              <ToolIcon tool={r.toolId} size={14} />
              <span className="mono min-w-0 flex-1 truncate text-small text-ink" title={key}>
                {key}
              </span>
              {r.ruleId && (
                <span className="hidden max-w-[16rem] truncate text-caption text-ink-4 lg:block" title={r.ruleId}>
                  {say(r.ruleId)}
                </span>
              )}
              {evidence && (
                <span className="mono hidden w-24 flex-none truncate text-caption text-ink-4 xl:block" title={evidence}>
                  {evidence.slice(0, 8)}
                </span>
              )}
            </div>
          );
        }
        const r = raw as unknown as StrayRow;
        return (
          <div key={`${key}:${i}`} className="flex items-center gap-3 border-b border-line px-4 py-1.5 last:border-0">
            <FileQuestion className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
            <span className="mono min-w-0 flex-1 truncate text-small text-ink" title={key}>
              {key}
            </span>
            <Stamp tone={r.reasonKind === 'CONTRADICTED_CANDIDATES' ? 'bad' : 'mut'}>{say(r.reasonKind)}</Stamp>
            {evidence && (
              <span className="mono hidden w-24 flex-none truncate text-caption text-ink-4 xl:block" title={evidence}>
                {evidence.slice(0, 8)}
              </span>
            )}
          </div>
        );
      })}

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2.5">
          {q.hasNextPage && (
            <Button variant="ghost" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
              {q.isFetchingNextPage ? 'Reading…' : `Load the next ${PAGE}`}
            </Button>
          )}
          <span className="text-caption text-ink-3">
            {q.hasNextPage
              ? `${rows.length.toLocaleString()} read so far, and there are more.`
              : `${rows.length.toLocaleString()} ${side === 'TOOL' ? 'traced' : 'untraced'} ${rows.length === 1 ? 'file' : 'files'}, which is all of them on this side of the ledger.`}
          </span>
        </div>
      )}
    </>
  );
}

/**
 * The rules the walk had available to it.
 *
 * A row in the ledger names the rule that matched. Without the registry beside
 * it that name is an identifier and nothing more, and the route serving it had
 * never been called from anywhere in the product.
 */
function RuleSet() {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['attribution-rules'],
    queryFn: ({ signal }) => backend.listAttributionRules(signal),
    enabled: open,
    retry: (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2),
    staleTime: 300_000,
  });
  const items = ((q.data as { items?: { ruleId?: string; method?: string; toolId?: string }[] } | undefined)?.items) ?? [];

  return (
    <div className="border-b border-line">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full px-4 py-2 text-left text-caption text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-ink"
      >
        {open ? 'Hide' : 'Show'} the rules this walk could match
      </button>
      {open && q.isPending && <Skeleton className="mx-4 mb-2 h-4" />}
      {open && q.isError && (
        <p className="px-4 pb-2 text-caption text-warn">{explainLine(q.error, 'read the rule set')}</p>
      )}
      {open && !q.isPending && !q.isError && (
        <div className="bg-panel-2">
          {items.map((r) => (
            <div key={r.ruleId} className="flex items-baseline gap-3 border-t border-line px-4 py-1 text-caption">
              <span className="mono min-w-0 flex-1 truncate text-ink-2">{r.toolId ?? r.ruleId}</span>
              <span className="text-ink-4">{r.method ? say(r.method) : ''}</span>
            </div>
          ))}
          <p className="border-t border-line px-4 py-1.5 text-caption text-ink-4">
            {items.length.toLocaleString()} rules. A file matching none of them is stray by definition rather than by
            failure.
          </p>
        </div>
      )}
    </div>
  );
}
